/**
 * Componente TOTPSetup
 * Il Diario di Bordo - SaaS
 *
 * Setup Google Authenticator / app TOTP.
 */

import { useState, useEffect } from 'react'
import { useTwoFactor } from '../../hooks/useTwoFactor'
import { Smartphone, Copy, Check, Loader2, AlertCircle, RefreshCw, Eye, EyeOff } from 'lucide-react'

export default function TOTPSetup({ onComplete, onCancel }) {
  const [step, setStep] = useState('loading') // loading, qr, verify, done
  const [qrCode, setQrCode] = useState(null)
  const [manualKey, setManualKey] = useState(null)
  const [tempSecret, setTempSecret] = useState(null)
  const [code, setCode] = useState('')
  const [showManualKey, setShowManualKey] = useState(false)
  const [copied, setCopied] = useState(false)
  const [backupCodes, setBackupCodes] = useState(null)
  
  const { setupTOTP, verifyTOTPSetup, loading, error, clearError } = useTwoFactor()

  useEffect(() => {
    initSetup()
  }, [])

  const initSetup = async () => {
    try {
      clearError()
      const data = await setupTOTP()
      setQrCode(data.qrCode)
      setManualKey(data.manualEntryKey)
      setTempSecret(data.tempSecret)
      setStep('qr')
    } catch (err) {
      console.error('Errore setup TOTP:', err)
    }
  }

  const handleCopyKey = async () => {
    try {
      await navigator.clipboard.writeText(manualKey.replace(/\s/g, ''))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Errore copia:', err)
    }
  }

  const handleVerify = async (e) => {
    e.preventDefault()
    
    if (code.length !== 6) return
    
    try {
      clearError()
      const data = await verifyTOTPSetup(code, tempSecret)
      setBackupCodes(data.backupCodes)
      setStep('done')
    } catch (err) {
      // Errore mostrato automaticamente
      setCode('')
    }
  }

  const handleComplete = () => {
    onComplete?.({ backupCodes })
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP: Loading
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (step === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600 mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Preparazione in corso...</p>
        
        {error && (
          <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center text-red-700 dark:text-red-400">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP: QR Code
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (step === 'qr') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 mb-4">
            <Smartphone className="h-6 w-6 text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Configura l'app di autenticazione
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Scansiona il codice QR con Google Authenticator o un'app simile
          </p>
        </div>

        {/* QR Code */}
        <div className="flex justify-center">
          <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-200">
            <img
              src={qrCode}
              alt="QR Code per TOTP"
              className="w-48 h-48"
            />
          </div>
        </div>

        {/* Manual Entry */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => setShowManualKey(!showManualKey)}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
          >
            {showManualKey ? (
              <>
                <EyeOff className="h-4 w-4 mr-1" />
                Nascondi codice manuale
              </>
            ) : (
              <>
                <Eye className="h-4 w-4 mr-1" />
                Non riesci a scansionare? Inserisci manualmente
              </>
            )}
          </button>
          
          {showManualKey && (
            <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Inserisci questo codice nell'app:
              </p>
              <div className="flex items-center justify-center space-x-2">
                <code className="px-3 py-2 bg-white dark:bg-gray-700 rounded font-mono text-sm tracking-wider">
                  {manualKey}
                </code>
                <button
                  type="button"
                  onClick={handleCopyKey}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  title="Copia"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Pulsanti */}
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 btn-secondary"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={() => setStep('verify')}
            className="flex-1 btn-primary"
          >
            Ho scansionato il codice
          </button>
        </div>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP: Verify
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (step === 'verify') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 mb-4">
            <Smartphone className="h-6 w-6 text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Verifica la configurazione
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Inserisci il codice a 6 cifre mostrato nell'app
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="sr-only">Codice di verifica</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '')
                setCode(value)
                clearError()
              }}
              placeholder="000000"
              className="input text-center text-2xl font-mono tracking-[0.5em] py-4"
              autoFocus
              autoComplete="one-time-code"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center text-red-700 dark:text-red-400">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => setStep('qr')}
              className="flex-1 btn-secondary"
            >
              Indietro
            </button>
            <button
              type="submit"
              disabled={code.length !== 6 || loading}
              className="flex-1 btn-primary disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Verifica...
                </>
              ) : (
                'Verifica'
              )}
            </button>
          </div>
        </form>

        {/* Riprova */}
        <div className="text-center">
          <button
            type="button"
            onClick={initSetup}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Genera nuovo QR code
          </button>
        </div>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP: Done
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (step === 'done') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
            <Check className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Configurazione completata!
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            L'autenticazione a due fattori è ora attiva
          </p>
        </div>

        {/* Backup codes */}
        {backupCodes && backupCodes.length > 0 && (
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">
              ⚠️ Codici di backup
            </h4>
            <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
              Salva questi codici in un luogo sicuro. Potrai usarli se perdi l'accesso al tuo dispositivo.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, i) => (
                <code
                  key={i}
                  className="px-3 py-2 bg-white dark:bg-gray-800 rounded text-sm font-mono text-center"
                >
                  {code}
                </code>
              ))}
            </div>
            <p className="mt-3 text-xs text-amber-600 dark:text-amber-400">
              Questi codici non verranno più mostrati.
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={handleComplete}
          className="w-full btn-primary"
        >
          Fatto
        </button>
      </div>
    )
  }

  return null
}
